import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { getRoleTone } from '@shared/config/theme';
import { StatusChip } from '@shared/components/StatusChip';

interface Invitation {
  id: string;
  organization_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
}

interface Member {
  id: string;
  organization_id: string;
  user_id: string;
  role: string;
  email: string;
  full_name: string;
  joined_at: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export default function TeamMembersContent() {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);

  const token = getToken();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchInvitations();
      fetchMembers();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/v1/organizations', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
        if (data.organizations && data.organizations.length > 0) {
          setSelectedOrg(data.organizations[0].id);
        }
      }
    } catch {
      setMessage({ type: 'error', text: t('team.errors.loadOrganizations') });
    }
  };

  const fetchInvitations = async () => {
    if (!selectedOrg) return;

    try {
      const response = await fetch(`/api/v1/invitations?organization_id=${selectedOrg}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch {
      setMessage({ type: 'error', text: t('team.errors.loadInvitations') });
    }
  };

  const fetchMembers = async () => {
    if (!selectedOrg) return;

    try {
      const response = await fetch(`/api/v1/members?organization_id=${selectedOrg}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
      }
    } catch {
      setMessage({ type: 'error', text: t('team.errors.loadMembers') });
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: selectedOrg,
          email: inviteEmail,
          role: inviteRole,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('team.messages.invited', { email: inviteEmail }) });
        setInviteEmail('');
        setInviteRole('MEMBER');
        setShowInviteForm(false);
        fetchInvitations();
      } else {
        setMessage({ type: 'error', text: t('team.errors.inviteFailed') });
      }
    } catch (_error) {
      setMessage({ type: 'error', text: t('team.errors.inviteFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (memberId: string) => {
    if (!newRole) return;

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`/api/v1/members/update-role?member_id=${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('team.messages.roleUpdated') });
        setEditingMember(null);
        setNewRole('');
        fetchMembers();
      } else {
        setMessage({ type: 'error', text: t('team.errors.roleUpdateFailed') });
      }
    } catch (_error) {
      setMessage({ type: 'error', text: t('team.errors.roleUpdateFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (member: Member) => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`/api/v1/members/remove?member_id=${member.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('team.messages.memberRemoved') });
        setShowRemoveModal(false);
        setMemberToRemove(null);
        fetchMembers();
      } else {
        setMessage({ type: 'error', text: t('team.errors.removeFailed') });
      }
    } catch (_error) {
      setMessage({ type: 'error', text: t('team.errors.removeFailed') });
    } finally {
      setLoading(false);
    }
  };

  const selectedOrgData = organizations.find((org) => org.id === selectedOrg);
  const formatRole = (role: string) => {
    const key = role.toLowerCase();
    const translated = t(`team.roles.${key}`);
    return translated === `team.roles.${key}` ? role : translated;
  };
  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value));
  const formatInvitationStatus = (status: string) => {
    const key = `team.invitationStatuses.${status.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };
  const pendingInvitations = invitations.filter((invitation) =>
    ['pending', 'sent'].includes(invitation.status.toLowerCase())
  );

  const canInvite =
    selectedOrgData && (selectedOrgData.role === 'OWNER' || selectedOrgData.role === 'ADMIN');

  return (
    <>
      {/* Organization Selector */}
      {organizations.length > 0 && (
        <div className="mb-6">
          <label
            htmlFor="org-select"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {t('team.selectOrganization')}
          </label>
          <select
            id="org-select"
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="block w-full max-w-md min-h-11 px-4 py-2 border border-border-strong-light dark:border-border-strong-dark rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({formatRole(org.role)})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Message */}
      {message && (
        <div
          role={message.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className={`mb-6 p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'
              : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Invite Button */}
      {canInvite && (
        <div className="mb-6">
          {!showInviteForm ? (
            <button
              onClick={() => setShowInviteForm(true)}
              className="min-h-11 px-4 py-2 text-sm font-semibold text-on-accent-light dark:text-on-accent-dark bg-brand-600 dark:bg-brand-400 rounded-xl hover:bg-brand-700 dark:hover:bg-brand-300"
            >
              {t('team.invite')}
            </button>
          ) : (
            <div className="bg-surface-card-light dark:bg-surface-card-dark p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-4">
                {t('team.inviteMember')}
              </h3>
              <form onSubmit={handleInvite}>
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                  >
                    {t('team.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    className="block w-full min-h-11 px-4 py-2 border border-border-strong-light dark:border-border-strong-dark rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                    placeholder={t('team.emailPlaceholder')}
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="role"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                  >
                    {t('team.role')}
                  </label>
                  <select
                    id="role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="block w-full min-h-11 px-4 py-2 border border-border-strong-light dark:border-border-strong-dark rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="OWNER">{t('team.roles.owner')}</option>
                    <option value="ADMIN">{t('team.roles.admin')}</option>
                    <option value="MEMBER">{t('team.roles.member')}</option>
                    <option value="VIEWER">{t('team.roles.viewer')}</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="min-h-11 px-4 py-2 text-sm font-semibold text-on-accent-light dark:text-on-accent-dark bg-brand-600 dark:bg-brand-400 rounded-xl hover:bg-brand-700 dark:hover:bg-brand-300 disabled:opacity-50"
                  >
                    {loading ? t('team.sending') : t('team.sendInvitation')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteEmail('');
                      setInviteRole('MEMBER');
                      setMessage(null);
                    }}
                    className="min-h-11 px-4 py-2 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Members List */}
      <div className="bg-surface-card-light dark:bg-surface-card-dark shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-surface-border-light dark:border-surface-border-dark">
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            {t('team.members')}
          </h2>
        </div>

        {members.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-slate-900 dark:text-white">
              {t('team.noMembers')}
            </h3>
            <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('team.invitePrompt')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
            {members.map((member) => {
              const isOwner = selectedOrgData?.role === 'OWNER';
              const canEdit = isOwner && editingMember !== member.id;

              return (
                <li key={member.id} className="px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-state-info-bg-light dark:bg-state-info-bg-dark flex items-center justify-center">
                          <span className="text-sm font-medium text-state-info-light dark:text-state-info-dark">
                            {member.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            {member.full_name}
                          </p>
                          <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                            {member.email}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-0 flex flex-wrap items-center gap-3 sm:ml-4 sm:gap-4">
                      {editingMember === member.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="min-h-11 px-3 py-1 border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                          >
                            <option value="OWNER">OWNER</option>
                            <option value="ADMIN">ADMIN</option>
                            <option value="MEMBER">MEMBER</option>
                            <option value="VIEWER">VIEWER</option>
                          </select>
                          <button
                            onClick={() => handleRoleUpdate(member.id)}
                            disabled={loading}
                            className="min-h-11 px-3 py-1 text-sm font-semibold text-on-accent-light dark:text-on-accent-dark bg-brand-600 dark:bg-brand-400 rounded-xl hover:bg-brand-700 dark:hover:bg-brand-300 disabled:opacity-50"
                          >
                            {loading ? t('team.saving') : t('common.save')}
                          </button>
                          <button
                            onClick={() => {
                              setEditingMember(null);
                              setNewRole('');
                            }}
                            className="min-h-11 px-3 py-1 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <StatusChip
                            tone={getRoleTone(member.role)}
                            label={formatRole(member.role)}
                          />
                          {canEdit && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingMember(member.id);
                                  setNewRole(member.role.toUpperCase());
                                }}
                                className="min-h-11 px-3 py-2 text-sm font-semibold text-state-info-light dark:text-state-info-dark hover:text-state-info-light dark:hover:text-state-info-dark"
                              >
                                {t('common.edit')}
                              </button>
                              <button
                                onClick={() => {
                                  setMemberToRemove(member);
                                  setShowRemoveModal(true);
                                }}
                                className="min-h-11 px-3 py-2 text-sm font-semibold text-state-danger-light dark:text-state-danger-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                              >
                                {t('common.remove')}
                              </button>
                            </>
                          )}
                        </>
                      )}
                      <p className="w-full text-xs text-text-muted-light dark:text-text-muted-dark sm:ml-auto sm:w-auto">
                        {t('team.joined', { date: formatDate(member.joined_at) })}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Invitations List */}
      <div className="bg-surface-card-light dark:bg-surface-card-dark shadow rounded-lg">
        <div className="px-6 py-4 border-b border-surface-border-light dark:border-surface-border-dark">
          <h2 className="text-lg font-medium text-slate-900 dark:text-white">
            {t('team.pendingInvitations')}
          </h2>
        </div>

        {pendingInvitations.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-slate-900 dark:text-white">
              {t('team.noPendingInvitations')}
            </h3>
            <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
              {canInvite ? t('team.invitePrompt') : t('team.adminInviteOnly')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
            {pendingInvitations.map((invitation) => (
              <li key={invitation.id} className="px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-10 w-10 text-text-subtle-light dark:text-text-subtle-dark"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {invitation.email}
                        </p>
                        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                          {t('team.role')}:{' '}
                          <span className="font-medium">{formatRole(invitation.role)}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-0 flex flex-wrap items-center gap-3 sm:ml-4 sm:gap-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-state-warn-bg-light dark:bg-state-warn-bg-dark text-state-warn-light dark:text-state-warn-dark">
                      {formatInvitationStatus(invitation.status)}
                    </span>
                    <p className="w-full text-xs text-text-muted-light dark:text-text-muted-dark sm:ml-auto sm:w-auto">
                      {t('team.expires', { date: formatDate(invitation.expires_at) })}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Remove Member Confirmation Modal */}
      {showRemoveModal && memberToRemove && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                {t('team.removeMember')}
              </h3>
              <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                {t('team.confirmRemoveText', {
                  name: memberToRemove.full_name,
                  email: memberToRemove.email,
                })}
              </p>
              <p className="mt-2 text-sm text-state-danger-light dark:text-state-danger-dark">
                {t('team.removeWarning')}
              </p>
            </div>
            <div className="px-6 py-4 bg-surface-2-light dark:bg-surface-2-dark flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setMemberToRemove(null);
                }}
                className="min-h-11 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleRemove(memberToRemove)}
                disabled={loading}
                className="min-h-11 px-4 py-2 text-sm font-medium text-on-accent-light dark:text-on-accent-dark bg-state-danger-light dark:bg-state-danger-dark rounded-xl hover:opacity-90 disabled:opacity-50"
              >
                {loading ? t('team.removing') : t('team.removeMember')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
